import { useVirtualizer } from '@tanstack/react-virtual';
import { Col, Spinner2, Text } from 'components';
import { memo, useCallback, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridTable, HeaderCell, HeaderRow, ROW_HEIGHT, Viewport } from './GridTable';
import { ListItem } from './';

// Rows above and below the visible window that are kept mounted, so a fast flick has already
// rendered what it scrolls onto instead of painting blank rows and catching up.
const OVERSCAN = 12;

// One entry per track in `COLUMNS`, in the same order — keeping them as a list makes a
// heading/column mismatch obvious instead of something you count by eye down the JSX. These are
// key suffixes rather than translated labels: the array is module-level, so resolving it here
// would freeze the headings at whatever language was active when the module first loaded.
const HEADINGS = [
  'media',
  'modules',
  'source',
  'uploader',
  'team',
  'status',
  'category',
  'inFeed',
  'uploadedAt'
];

// Hoisted so its identity is stable — as an inline arrow it would be a fresh component every
// render of the screen above.
const Header = () => {
  const { t } = useTranslation();

  return (
    <HeaderRow>
      {HEADINGS.map((field) => (
        // `center` is required, not decorative: every body cell centers its content, and `Text`
        // sets its own `text-align`, so HeaderCell's CSS alone leaves the heading left of the
        // column it labels.
        <HeaderCell key={field}>
          <Text center>{t(`admin.tiri.fields.${field}`)}</Text>
        </HeaderCell>
      ))}
    </HeaderRow>
  );
};

/**
 * The TIRI rows, windowed.
 *
 * The list has no upper bound — it pages for as long as the admin keeps scrolling — so
 * rendering every loaded row is what made the screen unscrollable: the DOM, the layout cost of
 * each append and the number of live thumbnails all grew with the number of pages fetched.
 * Only the rows on screen (plus OVERSCAN either side) are mounted now, so all three stay flat
 * however far you page.
 *
 * memo still matters: the screen above re-renders on summary fetches and filter edits, none of
 * which change the rows, and its callbacks are memoized so those renders stop at this boundary.
 */
const List = memo(({ data, onClick, dataId, loading, scrollEl }) => {
  // A callback ref rather than useRef, so measuring is driven by the node actually mounting.
  // The rows are absent for the first paint and for every filter change, and a plain ref would
  // still be null on the render that matters.
  const [rowsEl, setRowsEl] = useState(null);
  // Distance from the top of the scroll container's content down to the first row. The filters,
  // the summary strip and this list's own sticky header all sit above the rows inside the same
  // scroller, so without this the virtualizer places every row that far too high.
  const [scrollMargin, setScrollMargin] = useState(0);

  const count = !loading && data ? data.length : 0;

  const measure = useCallback(() => {
    if (!rowsEl || !scrollEl) return;

    setScrollMargin(
      rowsEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop
    );
  }, [rowsEl, scrollEl]);

  // Measured rather than assumed, because everything above the rows can change height after
  // mount: the summary strip swaps a spinner for its counts, and the team filter grows a chip
  // once a team is picked. Walking up to the scroller and observing each level's earlier
  // siblings catches all of those without the screen above having to know the list cares.
  useLayoutEffect(() => {
    measure();

    if (!rowsEl || !scrollEl || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(scrollEl);

    for (let node = rowsEl; node && node !== scrollEl; node = node.parentElement) {
      for (let prev = node.previousElementSibling; prev; prev = prev.previousElementSibling) {
        observer.observe(prev);
      }
    }

    return () => observer.disconnect();
  }, [measure, rowsEl, scrollEl]);

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollEl,
    // Exact, not an estimate: every row is a hard ROW_HEIGHT, which is why no row ever has to
    // be measured and the scroll height is correct from the first paint.
    estimateSize: () => ROW_HEIGHT,
    scrollMargin,
    overscan: OVERSCAN
  });

  return (
    <Col wid="100%" start noFlex>
      <GridTable>
        <Header />

        {!loading && data && (
          <Viewport ref={setRowsEl} style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = data[virtualRow.index];

              return (
                <ListItem
                  key={item._id || virtualRow.index}
                  item={item}
                  // A boolean, not dataId — passing the id would re-render every row whenever
                  // the selection changed.
                  isSelected={dataId === item._id}
                  onClick={onClick}
                  // `start` is measured from the top of the scroll container's content, so the
                  // row's offset inside the rows box is that minus where the box begins.
                  offsetTop={virtualRow.start - scrollMargin}
                />
              );
            })}
          </Viewport>
        )}
      </GridTable>

      {(loading || !data) && (
        <Col wid="calc(100vw - 200px)" marg="24px 0" centerAll noFlex>
          <Spinner2 />
        </Col>
      )}
    </Col>
  );
});

export default List;
